import axios from 'axios';
import apiClient from '../config/axiosConfig';

const validateCardId = (cardId) => {
  if (!cardId || typeof cardId !== 'string' || cardId.trim() === '') {
    throw new Error('Card ID is required and must be a valid string');
  }
  return cardId.trim();
};

const validateUserId = (userId) => {
  if (!userId || typeof userId !== 'string' || userId.trim() === '') {
    throw new Error('User ID is required and must be a valid string');
  }
  return userId.trim();
};

const validateSlug = (slug) => {
  if (!slug || typeof slug !== 'string' || slug.trim() === '') {
    throw new Error('Profile slug is required and must be a valid string');
  }
  return slug.trim();
};

const validateCardDetails = (cardDetails, operation = 'update') => {
  if (!cardDetails || typeof cardDetails !== 'object') {
    throw new Error('Card details must be a valid object');
  }

  // For update operations, allow partial updates - only validate fields that are present
  // Check if this is a partial update (only leadCapture fields or other non-contact_info fields)
  const isPartialUpdate = operation === 'update' && 
    (!cardDetails.contact_info || Object.keys(cardDetails.contact_info).length === 0) &&
    (cardDetails.leadCaptureFormHeader !== undefined || 
     cardDetails.leadCaptureInputFields !== undefined ||
     cardDetails.leadCaptureModeOn !== undefined ||
     cardDetails.cardName !== undefined ||
     cardDetails.cardLayout !== undefined ||
     cardDetails.cardTheme !== undefined ||
     cardDetails.linkColor !== undefined ||
     cardDetails.font !== undefined ||
     cardDetails.bio !== undefined ||
     cardDetails.profilePictureUrl !== undefined ||
     cardDetails.coverPhotoUrl !== undefined ||
     cardDetails.companyLogoUrl !== undefined ||
     cardDetails.matchLinkIconsToTheme !== undefined ||
     cardDetails.customLinkTypeLabels !== undefined ||
     cardDetails.is_default !== undefined ||
     cardDetails.autoFollowUpIntroEmail !== undefined);

  // Handle both nested and flat structures for backward compatibility
  const contactInfo = cardDetails.contact_info || cardDetails;
  const {
    fullName,
    phoneNumber,
    email,
    jobTitle,
    company,
    pronoun,
    userId,
    location,
    bio
  } = contactInfo;

  // Only require fullName if we're creating a card or updating contact_info
  if (!isPartialUpdate && (!fullName || typeof fullName !== 'string' || fullName.trim() === '')) {
    throw new Error('Full name is required and must be a valid string');
  }

  if (operation === 'create' && (!userId || typeof userId !== 'string' || userId.trim() === '')) {
    throw new Error('User ID is required for card creation');
  }

  if (email !== undefined && (typeof email !== 'string' || email.trim() === '')) {
    throw new Error('Email must be a valid string');
  }

  if (userId !== undefined && (typeof userId !== 'string' || userId.trim() === '')) {
    throw new Error('User ID must be a valid string');
  }

  if (phoneNumber !== undefined && phoneNumber !== null && typeof phoneNumber !== 'string') {
    throw new Error('Phone number must be a string');
  }

  if (jobTitle !== undefined && jobTitle !== null && typeof jobTitle !== 'string') {
    throw new Error('Job title must be a string');
  }

  if (company !== undefined && company !== null && typeof company !== 'string') {
    throw new Error('Company must be a string');
  }

  if (pronoun !== undefined && pronoun !== null && typeof pronoun !== 'string') {
    throw new Error('Pronoun must be a string');
  }

  if (location !== undefined && location !== null && typeof location !== 'string') {
    throw new Error('Location must be a string');
  }

  if (bio !== undefined && bio !== null && typeof bio !== 'string') {
    throw new Error('Bio must be a string');
  }

  const { is_default, cardLayout, cardTheme, cardName, linkColor } = cardDetails;

  if (is_default !== undefined && typeof is_default !== 'boolean') {
    throw new Error('is_default must be a boolean');
  }

  // For partial updates, return the data as-is without requiring contact_info
  if (isPartialUpdate) {
    return cardDetails;
  }

  const contactInfoToReturn = {
    fullName: fullName ? fullName.trim() : '',
    ...(phoneNumber !== undefined && phoneNumber !== null && { phoneNumber: phoneNumber.trim() }),
    ...(email !== undefined && email !== null && { email: email.trim() }),
    ...(jobTitle !== undefined && jobTitle !== null && { jobTitle: jobTitle.trim() }),
    ...(company !== undefined && company !== null && { company: company.trim() }),
    ...(pronoun !== undefined && pronoun !== null && { pronoun: pronoun.trim() }),
    ...(location !== undefined && location !== null && { location: location.trim() }),
  };

  return {
    ...cardDetails,
    contact_info: contactInfoToReturn
  };
};

const validateCardCreationDetails = (cardDetails) => {
  return validateCardDetails(cardDetails, 'create');
};

const validateCardUpdateDetails = (cardDetails) => {
  return validateCardDetails(cardDetails, 'update');
};

const validateStringValue = (value, fieldName) => {
  if (value !== null && value !== undefined && typeof value !== 'string') {
    throw new Error(`${fieldName} must be a string`);
  }
  return value;
};

const validateBooleanValue = (value, fieldName) => {
  if (value !== null && value !== undefined && typeof value !== 'boolean') {
    throw new Error(`${fieldName} must be a boolean`);
  }
  return value;
};

const validateArrayValue = (value, fieldName) => {
  if (value && !Array.isArray(value)) {
    throw new Error(`${fieldName} must be an array`);
  }
  return value || [];
};

const validateObjectValue = (value, fieldName) => {
  if (value && typeof value !== 'object') {
    throw new Error(`${fieldName} must be a valid object`);
  }
  return value;
};

const validateShareType = (shareType) => {
  if (!shareType || typeof shareType !== 'string' || shareType.trim() === '') {
    throw new Error('Share type is required and must be a valid string');
  }
  return shareType.trim();
};

const handleAuthorizationError = (error, operation = 'API operation') => {
  console.error(`[CardAPI] ${operation} Error:`, error.message);

  if (error.response?.status === 401) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    throw new Error('Your session has expired. Please log in again');
  }

  if (error.response?.status === 403) {
    throw new Error('You do not have permission to access this card');
  }

  if (error.response?.status === 404) {
    const isSignupFlow = localStorage.getItem('signupInProgress') === 'true';
    if (isSignupFlow) {
      throw new Error('Card not found during signup - this is expected');
    }
    throw new Error('Card not found or no longer available');
  }

  if (error.response?.status === 409) {
    throw new Error('Card already exists or conflicts with existing data');
  }

  if (error.response?.status === 422) {
    const validationErrors = error.response?.data?.errors || [];
    const errorMessage = validationErrors.length > 0
      ? `Validation errors: ${validationErrors.join(', ')}`
      : 'Invalid card data provided';
    throw new Error(errorMessage);
  }

  if (error.response?.status >= 500) {
    throw new Error('Server error. Please try again later.');
  }

  if (error.code === 'ERR_NETWORK') {
    throw new Error('Network error. Please check your connection and try again.');
  }

  if (error.code === 'ECONNABORTED') {
    throw new Error('Request timeout. Please try again.');
  }

  const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
  throw new Error(errorMessage);
};

export const createCard = async (cardDetails, updateCardId) => {
  try {
    const validCardDetails = validateCardDetails(cardDetails, 'create');
    const { userId } = validCardDetails;

    const response = await apiClient.post(`/api/card/`, validCardDetails);

    if (response.status === 201) {
      const newCardId = response.data.card._id;

      if (updateCardId) {
        updateCardId(newCardId);
      }

      return response.data;
    } else {
      throw new Error(`Failed to create card: Unexpected status code ${response.status}`);
    }
  } catch (error) {
    handleAuthorizationError(error, 'Creating card');
  }
};

export const updateCard = async (cardId, cardDetails) => {
  try {
    const validCardId = validateCardId(cardId);
    const validCardDetails = validateCardUpdateDetails(cardDetails);

    const response = await apiClient.put(`/api/card/update-card/${validCardId}`, validCardDetails);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update card with an unexpected status code');
    }
  } catch (error) {
    handleAuthorizationError(error, 'Updating card');
  }
};

export const getCardById = async (cardId) => {
  try {
    const validCardId = validateCardId(cardId);

    const response = await apiClient.get(`/api/card/${validCardId}`);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch card with an unexpected status code');
    }
  } catch (error) {
    handleAuthorizationError(error, 'Getting card by ID');
  }
};

export const updateCustomLabels = async (cardId, customLinkTypeLabels) => {
  try {
    const validCardId = validateCardId(cardId);
    const validCustomLabels = validateObjectValue(customLinkTypeLabels, 'Custom link type labels');

    const response = await apiClient.put(`/api/card/custom-labels/${validCardId}`, {
      customLinkTypeLabels: validCustomLabels
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update custom labels with an unexpected status code');
    }
  } catch (error) {
    handleAuthorizationError(error, 'Updating custom labels');
  }
};

export const setActiveCard = async (userId, cardId) => {
  try {
    const validUserId = validateUserId(userId);
    const validCardId = validateCardId(cardId);

    const response = await apiClient.post(`/api/card/setActiveCard`, {
      userId: validUserId,
      cardId: validCardId
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to set card as active with an unexpected status code');
    }
  } catch (error) {
    handleAuthorizationError(error, 'Setting active card');
  }
};

export const getCardLayouts = async () => {
  try {
    const response = await apiClient.get(`/api/card/card-layout-options`);

    if (response.status === 200) {
      return response.data.layouts;
    } else {
      throw new Error('Failed to fetch card layouts with an unexpected status code');
    }
  } catch (error) {
    handleAuthorizationError(error, 'Getting card layouts');
  }
};

export const updateCardProfilePictureUrl = async (cardId, profilePictureUrl) => {
  try {
    const validCardId = validateCardId(cardId);
    const validProfilePictureUrl = validateStringValue(profilePictureUrl, 'Profile picture URL');

    const response = await apiClient.put(`/api/card/update-card-profile-picture/${validCardId}`, {
      profilePictureUrl: validProfilePictureUrl
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update profile picture with an unexpected status code');
    }
  } catch (error) {
    handleAuthorizationError(error, 'Updating profile picture');
  }
};

export const updateCardCoverPhotoUrl = async (cardId, coverPhotoUrl) => {
  try {
    const validCardId = validateCardId(cardId);
    const validCoverPhotoUrl = validateStringValue(coverPhotoUrl, 'Cover photo URL');

    const response = await apiClient.put(`/api/card/update-card-cover-photo/${validCardId}`, {
      coverPhotoUrl: validCoverPhotoUrl
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update cover photo with an unexpected status code');
    }
  } catch (error) {
    handleAuthorizationError(error, 'Updating cover photo');
  }
};

export const updateCardCompanyLogoUrl = async (cardId, companyLogoUrl) => {
  try {
    const validCardId = validateCardId(cardId);
    const validCompanyLogoUrl = validateStringValue(companyLogoUrl, 'Company logo URL');

    const response = await apiClient.put(`/api/card/update-card-company-logo/${validCardId}`, {
      companyLogoUrl: validCompanyLogoUrl
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update company logo with an unexpected status code');
    }
  } catch (error) {
    handleAuthorizationError(error, 'Updating company logo');
  }
};

export const updateAutoFollowUpEmail = async (cardId, autoFollowUpIntroEmail) => {
  try {
    const validCardId = validateCardId(cardId);
    const validAutoFollowUp = validateBooleanValue(autoFollowUpIntroEmail, 'Auto follow-up email setting');

    const response = await apiClient.patch(`/api/card/update-auto-follow-up/${validCardId}`, {
      autoFollowUpIntroEmail: validAutoFollowUp
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update auto follow-up email setting with an unexpected status code');
    }
  } catch (error) {
    handleAuthorizationError(error, 'Updating auto follow-up email setting');
  }
};

export const updateLeadCaptureMode = async (cardId, leadCaptureModeOn) => {
  try {
    const validCardId = validateCardId(cardId);
    const validLeadCaptureMode = validateBooleanValue(leadCaptureModeOn, 'Lead capture mode setting');

    const response = await apiClient.patch(`/api/card/update-lead-capture/${validCardId}`, {
      leadCaptureModeOn: validLeadCaptureMode
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update lead capture mode setting with an unexpected status code');
    }
  } catch (error) {
    handleAuthorizationError(error, 'Updating lead capture mode setting');
  }
};

export const setDefaultCard = async (userId, cardId) => {
  try {
    const validUserId = validateUserId(userId);
    const validCardId = validateCardId(cardId);

    const response = await apiClient.post(
      `/api/card/set-default`,
      { userId: validUserId, cardId: validCardId }
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to set card as default with an unexpected status code');
    }

  } catch (error) {
    handleAuthorizationError(error, 'Setting default card');
  }
};

export const getDefaultCardByUserId = async (userId) => {
  try {
    const validUserId = validateUserId(userId);

    let lastError = null;
    const maxRetries = 3;
    const baseDelay = 1000;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await apiClient.get(`/api/user/${validUserId}/default-card`);

        if (response.status === 200) {
          return response.data;
        } else {
          throw new Error('Failed to fetch default card with an unexpected status code');
        }
      } catch (error) {
        lastError = error;

        if (attempt === maxRetries) {
          break;
        }

        const delay = baseDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    handleAuthorizationError(lastError, 'Getting default card by user ID');
  } catch (error) {
    handleAuthorizationError(error, 'Getting default card by user ID');
  }
};

export const getDefaultCardByProfileSlug = async (slug) => {
  try {
    const validSlug = validateSlug(slug);

    let lastError = null;
    const maxRetries = 3;
    const baseDelay = 1000;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await apiClient.get(`/api/user/profile/default-card/${validSlug}`);

        if (response.status === 200) {
          return response.data;
        } else {
          throw new Error('Failed to fetch default card with an unexpected status code');
        }
      } catch (error) {
        lastError = error;

        if (attempt === maxRetries) {
          break;
        }

        const delay = baseDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError;
  } catch (error) {
    throw error;
  }
};

export const updateCardShareCount = async (cardId, shareType) => {
  try {
    const validCardId = validateCardId(cardId);
    const validShareType = validateShareType(shareType);

    const response = await apiClient.post(`/api/card/share/${validCardId}`, {
      shareType: validShareType
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to track share with an unexpected status code');
    }
  } catch (error) {
    handleAuthorizationError(error, 'Tracking share');
  }
};

export const getCardShareStats = async (cardId) => {
  try {
    const validCardId = validateCardId(cardId);

    const response = await apiClient.get(`/api/card/share-stats/${validCardId}`);

    if (response.status === 200) {
      return response.data.stats;
    } else {
      throw new Error('Failed to fetch share statistics with an unexpected status code');
    }
  } catch (error) {
    handleAuthorizationError(error, 'Getting share statistics');
  }
};

export const updateCardContactLinks = async (cardId, linkIds) => {
  try {
    const validCardId = validateCardId(cardId);
    const validLinkIds = validateArrayValue(linkIds, 'Contact link IDs');

    const response = await apiClient.put(`/api/card/${validCardId}`, {
      contactLinks: validLinkIds
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update card contact links');
    }
  } catch (error) {
    handleAuthorizationError(error, 'Updating card contact links');
  }
};

export const duplicateCard = async (cardId, userId) => {
  try {
    const validCardId = validateCardId(cardId);
    const validUserId = validateUserId(userId);

    const response = await apiClient.post(
      `/api/card/${validCardId}/duplicate`,
      { userId: validUserId }
    );

    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error('Failed to duplicate card with an unexpected status code');
    }
  } catch (error) {
    handleAuthorizationError(error, 'Duplicating card');
  }
};

export const deleteCard = async (cardId, userId) => {
  try {
    const validCardId = validateCardId(cardId);
    const validUserId = validateUserId(userId);

    const response = await apiClient.delete(`/api/card/${validCardId}`, {
      data: { userId: validUserId }
    });

    if (response.status === 200) {
      return response.data;
    }
    throw new Error('Unexpected response status');
  } catch (error) {
    handleAuthorizationError(error, 'Deleting card');
  }
};